<template>
  <div>
    <h3 class="text-h3">{{ $t('account.orderDetails.title') }}</h3>
    <div class="order-details__header">
      <div class="order-details__date">
        {{ $t('account.orderDetails.orderedOn') }} <span>{{ formatDate(orderDetailsData.created) }}</span>
      </div>
      <div class="order-details__number">
        {{ $t('account.orderHistory.orderReference') }} # <span>{{ orderDetailsData.id }}</span>
      </div>
    </div>
    <ul class="order-details__info">
      <li class="order-details__info-item">
        <div class="order-details__info-title">{{ $t('account.orderDetails.shippingAddress') }}</div>
        <div class="order-details__address">
          <AddressItem :data="orderDetailsData.shippingAddress" />
          <ul class="order-details__address-additional-fields address-item">
            <li class="address-item__line">{{ orderDetailsData.customerUid }}</li>
          </ul>
        </div>
        <div v-if="orderDetailsData.deliveryMode" class="order-details__info-block">
          <div class="order-details__info-title">{{ $t('account.orderDetails.shippingMethod') }}</div>
          <div class="order-details__address">
            <ul>
              <li>{{ orderDetailsData.deliveryMode.title }}</li>
              <li>{{ orderDetailsData.deliveryMode.description }}</li>
              <li>{{ formatPrice(orderDetailsData.deliveryMode.price) }}</li>
            </ul>
          </div>
        </div>
      </li>
      <li class="order-details__info-item">
        <div class="order-details__info-title">{{ $t('account.orderDetails.paymentMethod') }}</div>
        <PaymentCard :data="orderDetailsData.paymentDetails"/>
      </li>
      <li class="order-details__info-item">
        <div class="order-details__info-title">{{ $t('account.orderDetails.orderSummary') }}</div>
        <ul class="summary__list">
          <li class="summary__item-wrapper">
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.numberOfItems') }}:</span>
              <span>{{ orderDetailsData.totalItems }}</span>
            </div>
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.subtotal') }}:</span>
              <span>{{ formatPrice(orderDetailsData.subTotal) }}</span>
            </div>
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.shipping') }}:</span>
              <span>{{ formatPrice(orderDetailsData.deliveryCost) }}</span>
            </div>
          </li>
          <li class="summary__item-wrapper summary__item-wrapper--big" v-if="orderDetailsData.totalDiscounts">
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.discounts') }}:</span>
              <span>- {{ formatPrice(orderDetailsData.totalDiscounts) }}</span>
            </div>
          </li>
          <li class="summary__item-wrapper">
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.totalBeforeTax') }}:</span>
              <span>{{ formatPrice(orderDetailsData.totalPrice) }}</span>
            </div>
            <div class="summary__item">
              <span>{{ $t('account.orderDetails.estimatedTax') }}:</span>
              <span>{{ formatPrice(orderDetailsData.totalTax) }}</span>
            </div>
          </li>
          <li class="summary__item-wrapper">
            <div class="summary__item text-h4">
              <span>{{ $t('account.orderDetails.total') }}:</span>
              <span>{{ formatPrice(orderDetailsData.totalPriceWithTax) }}</span>
            </div>
          </li>
        </ul>
        <div v-if="isReorderEnabled" class="order-details__summary-actions">
          <button
            @click="showModalReorder = true"
            :disabled="!orderDetailsData.entries.some(entry => entry.product.isPurchasable)"
            class="button button--primary"
          >{{ $t('account.orderHistory.orderAgain') }}</button>
        </div>
      </li>
    </ul>
    <div class="order-details__products">
      <table
        v-for="(entries, trackingId) in entriesByTrackingId"
        :key="trackingId"
        class="products__list table"
      >
        <thead class="products__header">
          <tr>
            <th class="text-left">
              <span class="products__status">
                <i class="icon" :class="getShippingStatus(orderDetailsData.status).iconClass"></i>
                <span>
                  {{ $t(`account.orderHistory.shippingStatuses.${getShippingStatus(orderDetailsData.status).key}`) }}
                </span>
              </span>
            </th>
            <template v-if="!isMobile">
              <th class="text-left">
                <span>{{ $t('account.orderDetails.productInfo') }}</span>
              </th>
              <th class="text-center">{{ $t('account.orderDetails.quantityOrdered') }}</th>
              <th class="text-center">{{ $t('account.orderDetails.quantityShipped') }}</th>
              <th>
                <div class="products__ship-date-title">
                  <span>{{ $t('account.orderDetails.estimatedShipDate') }}</span>
                  <i
                    class="icon-question-circle products__ship-date-icon"
                    v-tooltip="$t('account.orderDetails.estimatedShipDateInfo')"
                  />
                </div>
              </th>
              <th class="text-left">{{ $t('account.orderDetails.price') }}</th>
              <th></th>
            </template>
          </tr>
        </thead>
        <tbody class="products__body">
          <tr v-if="trackingId !== 'noTrackingId'" class="sub-header">
            <td colspan="7">
              <span>
                {{ $t('account.orderDetails.tracking') }}:
                <span v-html="trackingId" class="sub-header__tracking-id"></span>
              </span>
            </td>
          </tr>
          <tr
            v-for="entry in entries"
            :key="entry.product.sku"
            class="products__item"
          >
            <template v-if="!isMobile">
              <td class="products__image-col">
                <a
                  @click.prevent="handleProductLinkClick(entry.product.link)"
                  :href="entry.product.link"
                >
                  <img
                    :src="entry.product.image.previewSrc"
                    :alt="entry.product.image.title"
                    loading="lazy"
                    class="products__item-img"
                  />
                </a>
              </td>
              <td class="products__descr-col">
                <div class="products__item-info">
                  <div class="products__item-title">
                    <a
                      @click.prevent="handleProductLinkClick(entry.product.link)"
                      v-html="entry.product.title"
                      :href="entry.product.link"
                    ></a>
                  </div>
                  <div class="products__item-id">
                    {{ $t('cart.product.catalogId') }}: {{ entry.product.catalogId }}
                  </div>
                  <div class="products__item-desc" v-html="entry.product.descriptionShort"></div>
                </div>
              </td>
              <td class="products__item-qty text-center">{{ entry.quantity }}</td>
              <td class="products__item-qty text-center">{{ extractQuantityShipped(entry.shipDates) }}</td>
              <td class="products__item-qty text-center">
                <ul v-if="entry.shipDates">
                  <li
                    v-for="(shipQuantity, shipDate) in entry.shipDates"
                    :key="shipDate"
                    class="products__ship-date"
                  >{{ formatDate(shipDate, 'MM/DD/YYYY') }}</li>
                </ul>
                <span v-else>-</span>
              </td>
              <td class="products__item-price">
                <div class="product-line__price--single cart-product__price--single">
                  <p class="product-price">
                    <span
                      class="product-price__text"
                      :class="{'product-price__text--discount': entry.adjustedPrice && (entry.basePrice > entry.adjustedPrice)}"
                    >{{ formatPrice(entry.adjustedPrice || entry.basePrice) }}
                    </span>
                    <span
                      v-if="entry.adjustedPrice && (entry.basePrice > entry.adjustedPrice)"
                      class="product-price__text product-price__text--base"
                    >{{ formatPrice(entry.basePrice)  }}</span>
                    <span class="product-price__text">{{ $t('cart.product.each') }}</span>
                  </p>
                </div>
                <div class="products__item-total">
                  <p class="product-line__price cart-product__price">
                    <span
                      class="product-price__text"
                      :class="{'product-price__text--discount': entry.totalPriceWithoutDiscount > entry.totalPrice}"
                    >{{ formatPrice(entry.totalPrice) }}
                    </span>
                    <span
                      v-if="entry.totalPriceWithoutDiscount > entry.totalPrice"
                      class="product-price__text product-price__text--base"
                    >{{ formatPrice(entry.totalPriceWithoutDiscount)  }}</span>
                    <span class="product-price__text">{{ $t('cart.product.total') }}</span>
                  </p>
                </div>
              </td>
              <td v-if="isReorderEnabled" class="products__cart-button-col">
                <button
                  @click="onAddProductToCartClick(entry)"
                  :disabled="!entry.product.isPurchasable"
                  class="button button--primary button--add-to-cart"
                >
                  <i class="icon-shopping-cart"></i>
                  {{ $t('cart.productsList.addToCart') }}
                </button>
              </td>
            </template>
            <div class="cart-product" v-else>
              <div class="cart-product__img-wrap">
                <img
                  :src="entry.product.image.previewSrc || '/img-placeholder.svg'"
                  :alt="entry.product.image.title"
                  loading="lazy"
                  class="cart-product__img"
                />
              </div>
              <div class="cart-product__description">
                <a
                  @click.prevent="handleProductLinkClick(entry.product.link)"
                  v-html="entry.product.title"
                  :href="entry.product.link"
                  class="product-line__title cart-product__title"
                ></a>
                <p class="cart-product__sku">
                  {{ $t('cart.product.catalogId') }}: {{ entry.product.catalogId }}
                </p>
                <div class="products__item-desc" v-html="entry.product.descriptionShort"></div>

                <div class="cart-product__qty-wrap">
                  <p class="cart-product__qty-text">
                    <span>{{ $t('account.orderDetails.quantityOrdered') }}: {{ entry.quantity }}</span>
                  </p>
                  <p class="cart-product__qty-text">
                    <span>{{ $t('account.orderDetails.quantityShipped') }}: {{ extractQuantityShipped(entry.shipDates) }}</span>
                  </p>
                </div>

                <div class="cart-product__qty-wrap">
                  <i
                    class="icon-question-circle"
                    v-tooltip="$t('account.orderDetails.estimatedShipDateInfo')"
                  />
                  <span>{{ $t('account.orderDetails.estimatedShipDate') }}: </span>
                  <span v-if="entry.shipDates">
                    <span
                      v-for="(shipQuantity, shipDate) in entry.shipDates"
                      :key="shipDate"
                      class="products__ship-date"
                    >{{ formatDate(shipDate, 'MM/DD/YYYY') }}</span>
                  </span>
                  <span v-else>-</span>
                </div>

                <div class="cart-product__price-wrap">
                  <div class="product-line__price--single cart-product__price--single">
                    <p class="product-price">
                      <span
                        class="product-price__text"
                        :class="{'product-price__text--discount': entry.basePrice > entry.adjustedPrice}"
                      >{{ formatPrice(entry.adjustedPrice || entry.basePrice) }}
                      </span>
                      <span
                        v-if="entry.basePrice > entry.adjustedPrice"
                        class="product-price__text product-price__text--base"
                      >{{ formatPrice(entry.basePrice)  }}</span>
                      <span class="product-price__text">{{ $t('cart.product.each') }}</span>
                    </p>
                  </div>
                  <p class="product-line__price cart-product__price">
                    <span
                      class="product-price__text"
                      :class="{'product-price__text--discount': entry.totalPriceWithoutDiscount > entry.totalPrice}"
                    >{{ formatPrice(entry.totalPrice) }}
                    </span>
                    <span
                      v-if="entry.totalPriceWithoutDiscount > entry.totalPrice"
                      class="product-price__text product-price__text--base"
                    >{{ formatPrice(entry.totalPriceWithoutDiscount)  }}</span>
                    <span class="product-price__text">{{ $t('account.orderDetails.total') }}</span>
                  </p>
                </div>
                <button
                  @click="onAddProductToCartClick(entry)"
                  :disabled="!entry.product.isPurchasable"
                  class="button button--primary button--add-to-cart"
                >
                  <i class="icon-shopping-cart"></i>
                  {{ $t('cart.productsList.addToCart') }}
                </button>
              </div>
            </div>
          </tr>
        </tbody>
      </table>
    </div>

    <ModalReorder
      v-if="showModalReorder"
      :orderData="orderDetailsData"
      :showModal="showModalReorder"
      @close="showModalReorder = false"
    />

    <ModalCart
      v-if="showModalCart"
      @close="closeCartModal"
      @addProduct="onAddToCartFromModalCart"
      :productEntry="productEntryData"
      :showModalCart="showModalCart"
    />

    <ModalInventory
      :inventoryData="inventoryData"
      :showModal="showModalInventory"
      @close="closeInventoryModal"
    />

    <loader v-if="isLoading" />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

import FormatMixin from '@/mixins/FormatMixin'
import ShippingStatusesMixin from '@/mixins/ShippingStatusesMixin'
import PageMixin from '@/mixins/PageMixin'
import ScreenWidthMixin from '@/mixins/ScreenWidthMixin'
import NavigationalMixin from '@/mixins/NavigationalMixin'
import AddToCartMixin from '@/mixins/AddToCartMixin'
import LoaderMixin from '@/mixins/LoaderMixin'
import AddressItem from '@/components/AddressItem'
import PaymentCard from '@/components/PaymentCard'
import ModalReorder from '@/components/modal/ModalReorder'
import ModalCart from '@/components/modal/ModalCart'
import ModalInventory from '@/components/modal/ModalInventory'

export default {
  name: 'OrderDetails',
  components: {
    PaymentCard,
    AddressItem,
    ModalReorder,
    ModalCart,
    ModalInventory
  },
  mixins: [
    PageMixin,
    FormatMixin,
    ShippingStatusesMixin,
    ScreenWidthMixin,
    NavigationalMixin,
    AddToCartMixin,
    LoaderMixin
  ],
  props: {
    orderDetailsData: {
      type: Object,
      requried: true
    },
    isReorderEnabled: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    entriesByTrackingId () {
      const groups = {}
      this.orderDetailsData.entries.forEach(entry => {
        const trackingId = entry.trackingId || 'noTrackingId'

        if (!groups[trackingId]) {
          groups[trackingId] = []
        }

        groups[trackingId].push(entry)
      })
      return groups
    }
  },
  methods: {
    ...mapMutations('product', { setProductAnalyticsData: 'SET_PRODUCT_ANALYTICS_DATA' }),
    extractQuantityShipped (shipDates) {
      return shipDates
        ? Object.values(shipDates).reduce((acc, quantity) => acc + quantity, 0)
        : '-'
    },
    handleProductLinkClick (link) {
      this.setProductAnalyticsData({ referencePage: 'Order History' })
      this.navigateTo(link)
    },
    onAddProductToCartClick (entry) {
      this.withLoader(async () => {
        await this.addProductToCart({
          productData: entry.product,
          addToCartQty: entry.quantity,
          position: 0,
          pageName: 'Order History',
          searchQuery: ''
        })
      })
    },
    onAddToCartFromModalCart ({ productData, addToCartQty }) {
      this.withLoader(async () => {
        await this.addProductToCart({
          productData,
          addToCartQty,
          position: 0,
          pageName: 'Order History',
          searchQuery: ''
        })
      })
    }
  },
  data () {
    return {
      showModalReorder: false
    }
  }
}
</script>
